import axios, { HttpStatusCode, isAxiosError, } from 'axios';
import axiosRetry, { exponentialDelay } from 'axios-retry';
import { isNil } from 'lodash';
import { logger } from '@ninja/utilities/dist/Debug';
import { NetworkConnectionStatusCode, NoStatusCode, } from '../types';
import getUrlWithPathParams from './transform-url';
const convertResponseDataToJson = async (response, requestId) => {
    const hasContentData = (response === null || response === void 0 ? void 0 : response.data) instanceof Blob ? response.data.size : !!(response === null || response === void 0 ? void 0 : response.data);
    if (hasContentData && !(response === null || response === void 0 ? void 0 : response.headers['content-type'])) {
        logger.warn(requestId, '[Ninja][API-Client][convertResponseDataToJson]', 'content type header is missing in response');
        return response === null || response === void 0 ? void 0 : response.data;
    }
    if (hasContentData && (response === null || response === void 0 ? void 0 : response.headers['content-type'].includes('json'))) {
        if (response.data instanceof Blob) {
            return JSON.parse(await response.data.text());
        }
        else {
            return response.data;
        }
    }
    return response === null || response === void 0 ? void 0 : response.data;
};
export async function requestApi(baseURL, headers, requestSpecificHeaders, requestSpecificParameters, url, method, request, refreshToken, requestId, timeout, responseType, retryConfig, progressEventCallBack) {
    var _a, _b, _c, _d, _e, _f, _g;
    const urlWithParams = getUrlWithPathParams(url, requestSpecificParameters);
    const customHeaders = {};
    Object.keys(requestSpecificHeaders !== null && requestSpecificHeaders !== void 0 ? requestSpecificHeaders : {}).forEach((key) => {
        const value = requestSpecificHeaders === null || requestSpecificHeaders === void 0 ? void 0 : requestSpecificHeaders[key];
        if (value) {
            if (Array.isArray(value)) {
                customHeaders[key] = value.join(',');
            }
            else {
                customHeaders[key] = value;
            }
        }
    });
    const combinedHeaders = await Promise.all(Object.entries(Object.assign(Object.assign({}, headers), customHeaders)).map(async ([key, value]) => {
        if (typeof value === 'function')
            return [key, await value()];
        else
            return [key, value];
    }));
    const axiosRequestConfig = {
        baseURL,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        headers: combinedHeaders.reduce((acc, [k, v]) => ((acc[`${k}`] = v), acc), {}),
        url: urlWithParams,
        method,
        data: request,
        responseType,
        timeout,
        onUploadProgress(progressEvent) {
            const percentCompleted = progressEvent.total
                ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
                : 100;
            progressEventCallBack === null || progressEventCallBack === void 0 ? void 0 : progressEventCallBack(Object.assign(Object.assign({}, progressEvent), { percentage: percentCompleted }));
        },
    };
    logger.log(requestId, '[Ninja][API-Client][request]', axiosRequestConfig.url, {
        baseUrl: axiosRequestConfig.baseURL,
        method: axiosRequestConfig.method,
        data: axiosRequestConfig.data,
        headers: axiosRequestConfig.headers,
    });
    try {
        axiosRetry(axios, {
            retries: (_a = retryConfig === null || retryConfig === void 0 ? void 0 : retryConfig.retries) !== null && _a !== void 0 ? _a : 2,
            retryCondition: (_b = retryConfig === null || retryConfig === void 0 ? void 0 : retryConfig.retryCondition) !== null && _b !== void 0 ? _b : function retryCondition(error) {
                var _a;
                switch ((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) {
                    case HttpStatusCode.BadRequest:
                    case HttpStatusCode.Unauthorized:
                    case HttpStatusCode.PaymentRequired:
                    case HttpStatusCode.MethodNotAllowed:
                    case HttpStatusCode.UnprocessableEntity:
                    case HttpStatusCode.InternalServerError:
                        return false;
                    default:
                        return true;
                }
            },
            retryDelay: (_c = retryConfig === null || retryConfig === void 0 ? void 0 : retryConfig.retryDelay) !== null && _c !== void 0 ? _c : exponentialDelay,
        });
        const response = await axios.request(axiosRequestConfig);
        const data = await convertResponseDataToJson(response, requestId);
        logger.log(requestId, '[Ninja][API-Client][success]', axiosRequestConfig.url, {
            res: data,
            status: response.status,
        });
        const responseHeaders = isNil(response.headers) ? {} : response.headers;
        return { data, status: response.status, headers: responseHeaders };
    }
    catch (ex) {
        let status = NoStatusCode;
        let data = undefined;
        let responseHeaders = {};
        if (isAxiosError(ex)) {
            status = (_e = (_d = ex.response) === null || _d === void 0 ? void 0 : _d.status) !== null && _e !== void 0 ? _e : NetworkConnectionStatusCode;
            data = await convertResponseDataToJson(ex.response, requestId);
            responseHeaders = (_g = (_f = ex.response) === null || _f === void 0 ? void 0 : _f.headers) !== null && _g !== void 0 ? _g : {};
        }
        else {
            status = NoStatusCode;
        }
        if (status === NetworkConnectionStatusCode) {
            logger.error(requestId, '[Ninja][API-Client][error]', axiosRequestConfig.url, 'NetworkConnection');
            throw { mapped: [{ code: 'NetworkConnection' }], status };
        }
        switch (status) {
            case HttpStatusCode.Unauthorized: {
                logger.error(requestId, '[Ninja][API-Client][error]', axiosRequestConfig.url, 'Unauthorized');
                const newAccessToken = await (refreshToken === null || refreshToken === void 0 ? void 0 : refreshToken(headers, responseHeaders));
                if (newAccessToken) {
                    const newHeaders = Object.assign(Object.assign({}, headers), { Authorization: `Bearer ${newAccessToken}` });
                    return requestApi(baseURL, newHeaders, requestSpecificHeaders, requestSpecificParameters, url, method, request, undefined, // to try refresh only once
                    requestId, timeout, responseType);
                }
                else {
                    throw { mapped: [{ code: 'Unauthorized' }], status };
                }
            }
            case HttpStatusCode.MethodNotAllowed:
                logger.error(requestId, '[Ninja][API-Client][error]', axiosRequestConfig.url, 'MethodNotAllowed');
                throw { mapped: [{ code: 'MethodNotAllowed' }], status };
            case HttpStatusCode.InternalServerError:
                logger.error(requestId, '[Ninja][API-Client][error]', axiosRequestConfig.url, 'UnexpectedError');
                throw {
                    mapped: [{ code: 'UnexpectedError' }],
                    status,
                    originalResponse: data,
                };
            case NoStatusCode:
                // In case there was a javascript error
                throw { mapped: [{ code: 'UnexpectedError' }], originalResponse: ex === null || ex === void 0 ? void 0 : ex.toString(), status };
        }
        logger.error(requestId, '[Ninja][API-Client][error]', axiosRequestConfig.url, { res: ex, status });
        // return response data so it is validated and transformed by createAPIDefinition
        return { data, status, headers: responseHeaders };
    }
}
